import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import API from '../../services/API'
import { STAGES } from '../kit/helpers'
import { ContactFields, InterestField, ProfileFields } from './LeadFields'

const emptyLead = () => ({
  organisationName: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  source: '',
  stage: 'New',
  interestLevel: 3,
  mailingList: false,
  nextFollowUpAt: '',
  notes: '',
  staffNumbers: '',
  likelyTier: '',
  isCQCregistered: false,
  providesEventServices: false,
  providesPTS: false,
  providesCommercialTraining: false,
  existingChallenges: '',
})

const DUPLICATE_CHECK_DELAY_MS = 350

// Create a lead. The essentials are up front; the organisation profile is
// tucked behind "More details" so a quick capture takes seconds.
const LeadDialog = ({ open, onClose, onCreated }) => {
  const navigate = useNavigate()
  const [form, setForm] = useState(emptyLead)
  const [showMore, setShowMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // An existing lead or tenant with the same organisation name (server
  // normalises case, punctuation and Ltd/Limited). Checked as you type; the
  // create endpoint refuses duplicates with a 409 carrying the same shape.
  const [duplicate, setDuplicate] = useState(null)

  useEffect(() => {
    const name = form.organisationName.trim()
    if (!open || !name) {
      setDuplicate(null)
      return undefined
    }
    let cancelled = false
    const timer = setTimeout(() => {
      API.get('/admincrm/sales/duplicate-check', { params: { name } }).then((res) => {
        if (!cancelled && res.status === 200) setDuplicate(res.data?.duplicate ?? null)
      })
    }, DUPLICATE_CHECK_DELAY_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, form.organisationName])

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }))

  const close = () => {
    if (saving) return
    onClose()
    setForm(emptyLead())
    setShowMore(false)
    setError('')
    setDuplicate(null)
  }

  const viewDuplicate = () => {
    if (!duplicate) return
    const target = duplicate.leadId ? `/sales/${duplicate.leadId}` : `/tenants/${duplicate.tenantId}`
    close()
    navigate(target)
  }

  const submit = async (event) => {
    event?.preventDefault()
    if (!form.organisationName.trim()) {
      setError('Give the lead an organisation name.')
      return
    }
    setSaving(true)
    setError('')
    const res = await API.post('/admincrm/sales/leads', {
      ...form,
      nextFollowUpAt: form.nextFollowUpAt || null,
      likelyTier: form.likelyTier === '' ? null : form.likelyTier,
    })
    setSaving(false)
    if (res.status === 409 && res.data?.duplicate) {
      setDuplicate(res.data.duplicate)
      return
    }
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not save the lead.')
      return
    }
    const created = res.data
    setForm(emptyLead())
    setShowMore(false)
    onCreated?.(created)
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle>New lead</DialogTitle>
        <DialogContent>
          <Stack gap={2.5} paddingTop={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <ContactFields form={form} setField={setField} autoFocus />
            {duplicate && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={viewDuplicate}>
                    View it
                  </Button>
                }
              >
                <strong>{duplicate.organisationName}</strong> already exists
                {duplicate.type === 'tenant'
                  ? ' as a tenant'
                  : duplicate.archived
                    ? ' (archived)'
                    : ''}
                {duplicate.owner ? ` - owned by ${duplicate.owner.name}` : ''}.
              </Alert>
            )}

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Stage</InputLabel>
                  <Select
                    value={form.stage}
                    label="Stage"
                    onChange={(event) => setField('stage', event.target.value)}
                  >
                    {STAGES.filter((stage) => stage.key !== 'Lost').map((stage) => (
                      <MenuItem key={stage.key} value={stage.key}>
                        {stage.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Next follow-up"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.nextFollowUpAt}
                  onChange={(event) => setField('nextFollowUpAt', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InterestField
                  value={form.interestLevel}
                  onChange={(value) => setField('interestLevel', value)}
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              value={form.notes}
              onChange={(event) => setField('notes', event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />

            <Box>
              <Button
                size="small"
                onClick={() => setShowMore((prev) => !prev)}
                endIcon={showMore ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {showMore ? 'Hide organisation profile' : 'More details'}
              </Button>
              <Collapse in={showMore}>
                <Box paddingTop={1.5}>
                  <Typography variant="caption" color="text.secondary" display="block" marginBottom={1}>
                    Optional - helps size the opportunity.
                  </Typography>
                  <ProfileFields form={form} setField={setField} />
                </Box>
              </Collapse>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving || Boolean(duplicate)}>
            {saving ? 'Saving…' : 'Save lead'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default LeadDialog
